import { useState, useEffect, lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import { getMyAgentDashboards } from '@/api';
import LoginPage from '@/pages/LoginPage';
import ErrorBoundary from '@/components/ErrorBoundary';

// Code-split pages - loads each on first visit instead of one 835 kB bundle.
// lazyWithRetry: a failed chunk download (e.g. gateway restart mid-load) does NOT
// permanently poison the page. Plain React.lazy caches the rejected promise, so a
// single transient failure white-screens the tab until a full page reload.
const lazyWithRetry = <T extends object>(
  importer: () => Promise<{ default: React.ComponentType<T> }>,
  retries = 3,
  delayMs = 600,
) =>
  lazy(() =>
    importer().catch(async (err) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        await new Promise((r) => setTimeout(r, delayMs * attempt));
        try {
          return await importer();
        } catch {
          /* keep retrying */
        }
      }
      throw err;
    }),
  );

// Code-split pages â€” loads each on first visit instead of one 835 kB bundle
const Dashboard = lazyWithRetry(() => import('@/pages/Dashboard'));
const Deus = lazyWithRetry(() => import('@/pages/Deus'));
const SuperChat = lazyWithRetry(() => import('@/pages/SuperChat'));
const DeptChat = lazyWithRetry(() => import('@/pages/DeptChat'));
const Agents = lazyWithRetry(() => import('@/pages/Agents'));
const VisualBuilder = lazyWithRetry(() => import('@/pages/VisualBuilder'));
const Channels = lazyWithRetry(() => import('@/pages/Channels'));
const Governance = lazyWithRetry(() => import('@/pages/Governance'));
const Schedules = lazyWithRetry(() => import('@/pages/Schedules'));
const Settings = lazyWithRetry(() => import('@/pages/Settings'));
const ToolsPage = lazyWithRetry(() => import('@/pages/ToolsPage'));
const RBAC = lazyWithRetry(() => import('@/pages/RBAC'));
const Workflows = lazyWithRetry(() => import('@/pages/Workflows'));
const SelfImprove = lazyWithRetry(() => import('@/pages/SelfImprove'));
const MCP = lazyWithRetry(() => import('@/pages/MCP'));
const Watchdog = lazyWithRetry(() => import('@/pages/Watchdog'));
const Browser = lazyWithRetry(() => import('@/pages/Browser'));
const Logs = lazyWithRetry(() => import('@/pages/Logs'));
const Guardrails = lazyWithRetry(() => import('@/pages/GuardrailPage'));
const BridgeApprovals = lazyWithRetry(() => import('@/pages/BridgeApprovals'));
const DashboardAccess = lazyWithRetry(() => import('@/pages/DashboardAccess'));
const BusinessDashboard = lazyWithRetry(() => import('@/pages/BusinessDashboard'));


import { exchangeGmailCode } from '@/api';
import { Loader2, CheckCircle, XCircle, Lock } from 'lucide-react';

/** Handles Google OAuth redirect back to /admin/?gmail_oauth=1&code=... */
function GmailCallbackHandler() {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Completing Gmail setup...');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail_oauth') !== '1') return;

    const code = params.get('code');
    const state = params.get('state') || '';

    if (!code) {
      setStatus('error');
      setMessage('No authorization code received from Google.');
      return;
    }

    // Clean URL
    window.history.replaceState({}, '', '/admin/');

    (async () => {
      try {
        const result = await exchangeGmailCode(code, state);
        if (result.success) {
          setStatus('success');
          setMessage(`Gmail connected as ${result.email}`);
          // Notify the opener (Channels page)
          if (window.opener) {
            window.opener.postMessage({ type: 'gmail_connected', email: result.email }, window.location.origin);
            setTimeout(() => window.close(), 2000);
          }
        } else {
          setStatus('error');
          setMessage(`Error: ${result.error}`);
        }
      } catch (err: any) {
        setStatus('error');
        setMessage(`Error: ${err.message}`);
      }
    })();
  }, []);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-sm text-center dark:bg-slate-900">
        {status === 'processing' && (
          <><Loader2 size={40} className="animate-spin text-indigo-600 mx-auto mb-4 dark:text-indigo-400" /><p className="text-gray-700 dark:text-slate-300">{message}</p></>
        )}
        {status === 'success' && (
          <><CheckCircle size={40} className="text-green-500 mx-auto mb-4" /><p className="text-green-700 font-medium dark:text-green-300">{message}</p></>
        )}
        {status === 'error' && (
          <><XCircle size={40} className="text-red-500 mx-auto mb-4" /><p className="text-red-700 dark:text-red-300">{message}</p><p className="text-sm text-gray-500 mt-2 dark:text-slate-400">Close this tab and try again from Channels page.</p></>
        )}
      </div>
    </div>
  );
}

function AppShell() {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState(user?.role === 'chat_only' ? 'chat' : 'dashboard');

  // Landing tab: logins scoped to an agent dashboard (e.g. Founder) open on the
  // Business Dashboard instead of the generic company overview. Super admins and
  // chat-only users keep their existing landing tab. Runs once per session.
  const [landed, setLanded] = useState(false);
  useEffect(() => {
    if (!token || landed) return;
    if (user?.role === 'super_admin' || user?.role === 'chat_only') { setLanded(true); return; }
    let cancelled = false;
    getMyAgentDashboards()
      .then((r) => {
        if (cancelled) return;
        if ((r?.dashboards || []).length > 0) setActiveTab('bizdash');
        setLanded(true);
      })
      .catch(() => { if (!cancelled) setLanded(true); });
    return () => { cancelled = true; };
  }, [token, user?.role, landed]);

  // Check if this is a Gmail OAuth popup callback
  const isGmailCallback = window.location.search.includes('gmail_oauth=1');
  if (isGmailCallback) {
    return <GmailCallbackHandler />;
  }

  if (!token) return <LoginPage />;

  // 'user' role has no dashboard access
  if (user && user.role === 'user') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 dark:bg-slate-800/50">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 max-w-md text-center dark:bg-slate-900 dark:border-slate-700">
          <div className="w-12 h-12 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-4 dark:bg-indigo-500/10 dark:text-indigo-400"><Lock size={22} /></div>
          <h1 className="text-lg font-semibold text-gray-900 mb-1 dark:text-slate-100">No dashboard access</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400">Your account has the "user" role, which doesn't include dashboard access. Ask your administrator to upgrade the role.</p>
        </div>
      </div>
    );
  }

  const renderPage = () => {
    const isSuperChat = activeTab === 'chat' || activeTab === 'super-chat';
    return (
      <ErrorBoundary label={activeTab === 'mcp' ? 'MCP page' : undefined}>
      <Suspense fallback={
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
        </div>
      }>
      <>
        {isSuperChat ? null : (() => {
          switch (activeTab) {
            case 'dashboard': return <Dashboard />;
            case 'builder': return <VisualBuilder />;
            case 'deus': return <Deus />;
            case 'dept-chat': return <DeptChat />;
            case 'agents': return <Agents />;
            case 'channels': return <Channels />;
            case 'governance': return <Governance />;
            case 'schedules': return <Schedules />;
            case 'settings': return <Settings />;
            case 'rbac': return <RBAC />;
            case 'tools': return <ToolsPage />;
            case 'workflows': return <Workflows />;
            case 'selfimprove': return <SelfImprove />;
            case 'mcp': return <MCP />;
            case 'watchdog': return <Watchdog />;
            case 'browser': return <Browser />;
            case 'logs': return <Logs />;
            case 'guardrails': return <Guardrails />;
            case 'bridge': return <BridgeApprovals />;
            case 'dash-access': return <DashboardAccess />;
            case 'bizdash': return <BusinessDashboard />;
            default: return <Dashboard />;
          }
        })()}
        <div style={{ display: isSuperChat ? 'block' : 'none' }}>
          <SuperChat isVisible={isSuperChat} />
        </div>
      </>
      </Suspense>
      </ErrorBoundary>
    );
  };

  return (
    <Layout activeTab={activeTab} onTabChange={setActiveTab}>
      {renderPage()}
    </Layout>
  );
}

// ── Dashboard presence heartbeat ─────────────────────────────────────────────
// The gateway keeps heavy background services (local embedding server, browser
// connector relay) alive ONLY while an admin dashboard is open. This beats the
// heartbeat every 15s and sends a final "closed" ping on pagehide.
function useDashboardPresence() {
  useEffect(() => {
    const ping = (closed = false) => {
      const t = localStorage.getItem('babuba_token');
      if (!t) return;
      fetch('/api/dashboard/presence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify({ closed }),
        keepalive: true,
      }).catch(() => {});
    };
    ping(false);
    const iv = window.setInterval(() => ping(false), 15000);
    const onVisible = () => { if (document.visibilityState === 'visible') ping(false); };
    const onHide = () => ping(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pagehide', onHide);
    return () => {
      window.clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pagehide', onHide);
    };
  }, []);
}

export default function App() {
  useDashboardPresence();
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
